import { useEffect, useState } from 'react';
import { Link, useRoute, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPortalCampaigns,
  fetchPortalContext,
  fetchPortalCycles,
} from '@/lib/portalApi';
import { PortalShell } from './PortalShell';
import { PortalCampaignGalleryPage } from './PortalCampaignGalleryPage';
import { PortalCockpitPage } from './PortalCockpitPage';
import { PortalCompliancePage } from './PortalCompliancePage';
import { PortalCyclesPage } from './PortalCyclesPage';
import { PortalStoresPage } from './PortalStoresPage';
import { PortalCampaignWorkspacePage } from './PortalCampaignWorkspacePage';
import {
  buildPortalHref,
  parsePortalCycleId,
  parsePortalPositiveId,
} from './portalCycleQuery';
import { usePortalCycleSelection } from './usePortalCycleSelection';
import {
  PortalErrorBlock,
  PortalLoadingBlock,
  PortalCyclePickerFields,
  portalCardClass,
  portalLabelClass,
  portalLinkClass,
  portalPageSubtitleClass,
  portalPageTitleClass,
  portalProgressFillClass,
  portalProgressTrackClass,
  portalTableClass,
  portalTableHeadClass,
} from './portalUi';

function parseRouteId(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function PaginationBar({
  page,
  pageSize,
  totalCount,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}) {
  if (totalCount <= pageSize) return null;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
      <span>
        Showing {start}–{end} of {totalCount}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="rounded border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function PortalHome() {
  const urlSearch = useSearch();
  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });
  const {
    data: cyclesData,
    isLoading: cyclesLoading,
    isError: cyclesError,
    error: cyclesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'cycles'],
    queryFn: fetchPortalCycles,
    enabled: !!context,
  });

  const { selectedCycleId, onCycleChange } = usePortalCycleSelection(cyclesData?.items);
  const [search, setSearch] = useState('');
  const [campaignPage, setCampaignPage] = useState(1);
  const campaignPageSize = 25;

  useEffect(() => {
    setCampaignPage(1);
  }, [selectedCycleId, search]);

  const {
    data: campaigns,
    isLoading: campaignsLoading,
    isError: campaignsError,
    error: campaignsErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'campaigns', selectedCycleId, search, campaignPage, campaignPageSize],
    queryFn: () => fetchPortalCampaigns(selectedCycleId!, {
      search,
      page: campaignPage,
      pageSize: campaignPageSize,
    }),
    enabled: selectedCycleId != null,
  });

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    const message = queryErrorMessage(
      contextErrorDetail,
      'Could not load portal context. Check with your administrator.',
    );
    return <PortalErrorBlock message={message} />;
  }

  if (cyclesLoading) {
    return (
      <PortalShell context={context}>
        <PortalLoadingBlock />
      </PortalShell>
    );
  }

  if (cyclesError) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock
          message={queryErrorMessage(cyclesErrorDetail, 'Could not load cycles. Please try again.')}
        />
      </PortalShell>
    );
  }

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div>
          <h2 className={portalPageTitleClass}>Campaigns</h2>
          <p className={portalPageSubtitleClass}>Track activation progress for your stores.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <PortalCyclePickerFields
            cycles={cyclesData?.items ?? []}
            selectedCycleId={selectedCycleId}
            onCycleChange={onCycleChange}
            externalSelection
            disabled={cyclesLoading || cyclesError}
            errorMessage={cyclesError
              ? queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')
              : undefined}
          />
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-sm">
            <span className={portalLabelClass}>Search</span>
            <input
              className="rounded border border-border bg-background px-3 py-2"
              placeholder="Campaign name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>

        {cyclesError ? (
          <PortalErrorBlock
            message={queryErrorMessage(cyclesErrorDetail, 'Could not load cycles. Please try again.')}
          />
        ) : campaignsLoading ? (
          <PortalLoadingBlock />
        ) : campaignsError ? (
          <PortalErrorBlock
            message={queryErrorMessage(campaignsErrorDetail, 'Could not load campaigns. Please try again.')}
          />
        ) : (
          <div className={portalCardClass}>
            <table className={portalTableClass}>
              <thead className={portalTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Stores</th>
                  <th className="px-4 py-3">Progress</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {(campaigns?.items ?? []).map((campaign) => (
                  <tr key={campaign.id} className="border-t">
                    <td className="px-4 py-3 font-medium">{campaign.name}</td>
                    <td className="px-4 py-3">
                      {campaign.completedStoreCount}/{campaign.storeCount}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={portalProgressTrackClass}>
                          <div
                            className={portalProgressFillClass}
                            style={{ width: `${campaign.completionPercent}%` }}
                          />
                        </div>
                        <span>{campaign.completionPercent}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        href={buildPortalHref(`/portal/campaigns/${campaign.id}`, urlSearch, {
                          cycleId: selectedCycleId ?? undefined,
                        })}
                        className={portalLinkClass}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {(campaigns?.items ?? []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      No campaigns found for this cycle.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {campaigns ? (
              <PaginationBar
                page={campaigns.page}
                pageSize={campaigns.pageSize}
                totalCount={campaigns.totalCount}
                onPageChange={setCampaignPage}
              />
            ) : null}
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function PortalCampaignDetail() {
  const [, params] = useRoute('/portal/campaigns/:campaignId');
  const search = useSearch();
  const campaignId = parseRouteId(params?.campaignId);
  const cycleId = parsePortalCycleId(search);

  const {
    data: context,
    isLoading: contextLoading,
    isError: contextError,
    error: contextErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return (
      <PortalErrorBlock
        message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')}
      />
    );
  }

  if (campaignId == null || cycleId == null) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Invalid campaign link. Open a campaign from the portal home page." />
      </PortalShell>
    );
  }

  return <PortalCampaignWorkspacePage context={context} campaignId={campaignId} cycleId={cycleId} />;
}

export function PortalRoutes() {
  const [homeMatch] = useRoute('/portal');
  const [cyclesMatch] = useRoute('/portal/cycles');
  const [storesMatch] = useRoute('/portal/stores');
  const [cockpitMatch] = useRoute('/portal/cockpit');
  const [complianceMatch] = useRoute('/portal/compliance');
  const [portalGalleryMatch] = useRoute('/portal/gallery');
  const [detailMatch] = useRoute('/portal/campaigns/:campaignId');
  const [galleryMatch] = useRoute('/portal/campaigns/:campaignId/gallery');

  if (galleryMatch || portalGalleryMatch) return <PortalCampaignGalleryPage />;
  if (detailMatch) return <PortalCampaignDetail />;
  if (storesMatch) return <PortalStoresPage />;
  if (cyclesMatch) return <PortalCyclesPage />;
  if (cockpitMatch) return <PortalCockpitPage />;
  if (complianceMatch) return <PortalCompliancePage />;
  if (homeMatch) return <PortalHome />;
  return <PortalHome />;
}
